if (!customElements.get('cart-drawer-gift-selector')) {
  customElements.define(
    'cart-drawer-gift-selector',
    class CartDrawerGiftSelector extends HTMLElement {
      connectedCallback() {
        this._initUI();
        this._attachLiveTierListener();
        // If the page has already seen a cart:updated (or computed an initial
        // total), reconcile this instance *now* so a newly-inserted selector
        // (snapshot-restored, refreshGiftSelectorFromSection's replaceWith, or
        // any other section swap) never shows a stale "Spend $X more" amount
        // while it waits for the next cart mutation.
        try {
          const cached = window.__lastCartTotalCents;
          if (typeof cached === 'number') {
            this._recomputeTierEligibility(cached);
          } else if (typeof routes !== 'undefined' && routes.cart_url) {
            // First-page-load safety net: the server may have rendered the
            // locked CTA text against a stale cart.total_price (Section Render
            // API cache, concurrent mutation, etc.). Before we've observed a
            // cart:updated event we have no cached total to reconcile against,
            // so fetch the authoritative cart total once and recompute. This
            // is fire-and-forget — it must never block initial render.
            fetch(`${routes.cart_url}.js`)
              .then((r) => r.json())
              .then((cartData) => {
                try {
                  if (cartData && typeof cartData.total_price === 'number') {
                    const q =
                      typeof window.qualifyingSpendCentsFromCart === 'function'
                        ? window.qualifyingSpendCentsFromCart(cartData)
                        : cartData.total_price;
                    window.__lastCartTotalCents = q;
                    if (Array.isArray(cartData.items)) {
                      this._syncHeaderClaimBadgeFromCart(cartData.items);
                    }
                    this._recomputeTierEligibility(q);
                  }
                } catch (_) {}
              })
              .catch((err) => { if (window.DEBUG_CART) console.warn('[cart] gift-selector: /cart.js tier refetch failed', err); });
          }
        } catch (_) {}
      }

      disconnectedCallback() {
        this._giftUiBound = false;
        if (this._onDelegatedGiftClickBound) {
          this.removeEventListener('click', this._onDelegatedGiftClickBound);
          this._onDelegatedGiftClickBound = null;
        }
        if (this._onCartUpdatedLive) {
          document.removeEventListener('cart:updated', this._onCartUpdatedLive);
          this._onCartUpdatedLive = null;
        }
      }

      /**
       * Listen for cart:updated and recompute each tier's locked/eligible state
       * based on the new cart total — no section refetch, no page refresh.
       * Crossing a threshold flips a slide from "Spend $X more" → "Claim gift →"
       * instantly. The server-rendered HTML still drives the initial state; this
       * only toggles between locked and eligible based on total. A claimed tier
       * stays claimed regardless.
       */
      _attachLiveTierListener() {
        if (this._onCartUpdatedLive) return;
        this._onCartUpdatedLive = (e) => {
          try {
            const detail = e && e.detail;
            const cartData = detail && detail.cartData;
            let totalCents = null;
            if (cartData && typeof cartData.total_price === 'number') {
              totalCents =
                typeof window.qualifyingSpendCentsFromCart === 'function'
                  ? window.qualifyingSpendCentsFromCart(cartData)
                  : cartData.total_price;
            } else if (cartData && typeof cartData.items_subtotal_price === 'number') {
              totalCents =
                typeof window.qualifyingSpendCentsFromCart === 'function'
                  ? window.qualifyingSpendCentsFromCart(cartData)
                  : cartData.items_subtotal_price;
            }
            // Bail FIRST if we don't have an authoritative total. Events
            // without total_price (e.g. /cart/add.js responses that only
            // contain the ADDED items, or cart:updated dispatched with no
            // detail) would otherwise trick _reconcileClaimedAgainstCart
            // into thinking the user's previously-claimed gifts are gone
            // — because cartData.items in that case is NOT the full cart.
            // cart.js' cart:updated normalizer will re-dispatch this event
            // with full cart data shortly after; we handle it then.
            if (totalCents === null) return;

            // Reconcile claimed state: if a tier is marked claimed in the
            // DOM but its gift line item is no longer in cart (user hit ×
            // on the gift), flip it out of claimed so the eligibility
            // recompute can promote it back to "Claim gift" (or locked)
            // from scratch.
            let forceSlides = null;
            if (Array.isArray(cartData.items)) {
              forceSlides = this._reconcileClaimedAgainstCart(cartData.items);
            }

            // Cache the latest known cart total so any gift selector that
            // mounts AFTER this event (e.g. via refreshGiftSelectorFromSection's
            // async replaceWith) can self-reconcile its locked CTA amount in
            // connectedCallback instead of displaying stale text.
            window.__lastCartTotalCents = totalCents;
            if (Array.isArray(cartData.items)) {
              this._syncHeaderClaimBadgeFromCart(cartData.items);
            }
            this._recomputeTierEligibility(totalCents, forceSlides);
          } catch (_) {}
        };
        document.addEventListener('cart:updated', this._onCartUpdatedLive);
      }

      /**
       * Toggle heading badge: ✓ when any gift line for this block exists, else tier count.
       * Keeps UI aligned when the customer removes the gift (server HTML can lag behind AJAX).
       */
      _syncHeaderClaimBadgeFromCart(cartItems) {
        const blockId = this.dataset.blockId || '';
        if (!blockId) return;
        const countEl = this.querySelector('.cart-drawer-gift__count');
        if (!countEl) return;
        const slideTotal = parseInt(this.dataset.slideTotal || '0', 10) || 0;
        const hasGift =
          Array.isArray(cartItems) &&
          cartItems.some((it) => {
            const p = it && it.properties;
            if (!p) return false;
            const v = p._drawer_gift_selector != null ? p._drawer_gift_selector : p['_drawer_gift_selector'];
            return String(v) === String(blockId);
          });
        if (hasGift) {
          countEl.textContent = '✓';
          countEl.classList.add('cart-drawer-gift__count--claimed');
          this.classList.add('cart-drawer-gift--has-claimed');
          this.dataset.claimed = 'true';
        } else {
          countEl.textContent = slideTotal > 0 ? String(slideTotal) : '';
          countEl.classList.remove('cart-drawer-gift__count--claimed');
          this.classList.remove('cart-drawer-gift--has-claimed');
          this.dataset.claimed = 'false';
        }
      }

      /**
       * For every slide currently marked claimed, verify the matching gift
       * line item still exists in the cart. If not, strip the claimed state
       * (dataset + classes) so the subsequent eligibility recompute treats
       * this tier as fresh — it'll flip to "Claim gift →" if the threshold
       * is still met, or back to "Spend $X more" if not.
       */
      _reconcileClaimedAgainstCart(cartItems) {
        const blockId = this.dataset.blockId || '';
        const slides = this.querySelectorAll('[data-gift-tier-slide]');
        const forced = new Set();
        if (!slides || !slides.length) return forced;
        slides.forEach((slide) => {
          if (slide.dataset.tierClaimed !== 'true') return;
          const tierIdx = String(slide.dataset.tierIndex || '1');
          const hasGift = cartItems.some((it) => {
            const props = it && it.properties;
            if (!props) return false;
            if (props._drawer_gift_selector !== blockId) return false;
            const liTier = String(props._drawer_gift_tier_index || '1');
            return liTier === tierIdx;
          });
          if (hasGift) return;

          slide.dataset.tierClaimed = 'false';
          const teaser = slide.querySelector('[data-tier-teaser]');
          if (!teaser) return;
          teaser.classList.remove('cart-drawer-gift__teaser--claimed');
          teaser.removeAttribute('data-gift-teaser-selected');
          teaser.setAttribute('data-gift-teaser', '');
          const card = teaser.querySelector('.cart-drawer-gift__teaser-card');
          if (card) {
            card.classList.remove('cart-drawer-gift__teaser-card--claimed');
          }
          const cta = teaser.querySelector('.cart-drawer-gift__teaser-cta');
          if (cta) {
            cta.classList.remove('cart-drawer-gift__teaser-cta--claimed');
          }
          // Temporarily force eligibility mismatch so _recomputeTierEligibility
          // will actually apply the eligible/locked transition next.
          slide.dataset.tierEligible = 'false';
          forced.add(slide);
        });
        return forced;
      }

      _recomputeTierEligibility(totalCents, forceSlides) {
        const slides = this.querySelectorAll('[data-gift-tier-slide]');
        if (!slides || !slides.length) return;
        slides.forEach((slide) => {
          // Claimed stays claimed — don't flip it to locked if total dips.
          if (slide.dataset.tierClaimed === 'true') return;
          const threshold = parseInt(slide.dataset.tierThresholdCents, 10);
          if (!threshold && threshold !== 0) return;
          const shouldBeEligible = totalCents >= threshold;
          const isEligible = slide.dataset.tierEligible === 'true';
          const forced = forceSlides && forceSlides.has(slide);
          const stateFlipped = shouldBeEligible !== isEligible || forced;

          const teaser = slide.querySelector('[data-tier-teaser]');
          if (!teaser) return;

          if (shouldBeEligible) {
            // Tier is now (or stays) eligible. Only run the DOM transition
            // when state actually flipped or was forced — once the eligible
            // card is mounted there's nothing dollar-amount-related to
            // refresh ("Choose your gift →" doesn't depend on cart total).
            if (!stateFlipped) return;

            slide.dataset.tierEligible = 'true';
            teaser.classList.remove('cart-drawer-gift__teaser--locked');
            teaser.classList.add('cart-drawer-gift__teaser--eligible');
            const card = teaser.querySelector('.cart-drawer-gift__teaser-card');
            if (card && card.tagName === 'DIV') {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = card.className
                .replace('cart-drawer-gift__teaser-card--locked', 'cart-drawer-gift__teaser-card--eligible');
              btn.setAttribute('data-open-gift-chooser', '');
              if (slide.dataset.tierIndex) btn.setAttribute('data-tier-index', slide.dataset.tierIndex);
              btn.innerHTML = card.innerHTML;
              card.replaceWith(btn);
            }
            const cta = teaser.querySelector('.cart-drawer-gift__teaser-cta');
            if (cta) {
              cta.classList.remove('cart-drawer-gift__teaser-cta--locked');
              cta.classList.add('cart-drawer-gift__teaser-cta--eligible');
              const label = (this.dataset.teaserCta || 'Claim gift') + ' \u2192';
              cta.textContent = label;
            }
          } else {
            // Tier is locked. ALWAYS recompute the "Spend $X more" amount
            // even if state hasn't changed — the dollar number depends on
            // the live cart total, not the binary locked/eligible flag.
            // Without this branch the displayed amount stays stale until
            // the full server re-render lands (and even longer when cart.js
            // restores the gift snapshot instead of using the fresh render).
            slide.dataset.tierEligible = 'false';

            // Only do the eligible→locked structural swap when it actually flipped.
            if (stateFlipped) {
              teaser.classList.remove('cart-drawer-gift__teaser--eligible');
              teaser.classList.add('cart-drawer-gift__teaser--locked');
              const card = teaser.querySelector('.cart-drawer-gift__teaser-card');
              if (card && card.tagName === 'BUTTON') {
                const div = document.createElement('div');
                div.className = card.className
                  .replace('cart-drawer-gift__teaser-card--eligible', 'cart-drawer-gift__teaser-card--locked');
                div.setAttribute('role', 'group');
                div.innerHTML = card.innerHTML;
                card.replaceWith(div);
              }
            }

            // CTA dollar text — refresh on EVERY cart update (this is the fix).
            const cta = teaser.querySelector('.cart-drawer-gift__teaser-cta');
            if (cta) {
              cta.classList.remove('cart-drawer-gift__teaser-cta--eligible');
              cta.classList.add('cart-drawer-gift__teaser-cta--locked');
              const remainingCents = Math.max(0, threshold - totalCents);
              const fmt = this._formatMoney(remainingCents);
              const template = this.dataset.lockedTemplate || 'Spend [amount] more';
              const nextText = template.replace('[amount]', fmt);
              if (cta.textContent !== nextText) cta.textContent = nextText;
              // aria-label on the locked card mirrors the CTA copy — keep it in sync
              // so screen readers don't announce a stale dollar amount.
              const lockedCard = teaser.querySelector('.cart-drawer-gift__teaser-card--locked');
              if (lockedCard) lockedCard.setAttribute('aria-label', nextText);
            }
          }
        });

        // If a carousel is active, let it re-read slide state so active-slide
        // visual treatments stay aligned.
        if (this.dataset.useCarousel === 'true' && typeof window.initGiftTierCarousel === 'function') {
          try { window.initGiftTierCarousel(this); } catch (_) {}
        }
      }

      _formatMoney(cents) {
        if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
          try { return window.Shopify.formatMoney(cents, window.Shopify.money_format); } catch (_) {}
        }
        const dollars = (Math.max(0, cents) / 100).toFixed(2);
        return '$' + dollars;
      }

      _handleDelegatedGiftClick(e) {
        const claimBtn = e.target.closest('[data-gift-claim]');
        if (claimBtn) {
          e.preventDefault();
          this.claimGift(claimBtn);
          return;
        }

        const variantBtn = e.target.closest('[data-gift-variant-pick]');
        if (variantBtn) {
          e.preventDefault();
          if (variantBtn.disabled || variantBtn.dataset.disabled === 'true') return;
          const article = variantBtn.closest('[data-gift-item]');
          if (!article) return;
          article.querySelectorAll('[data-gift-variant-pick]').forEach((b) => b.classList.remove('is-selected'));
          variantBtn.classList.add('is-selected');
          const claim = article.querySelector('[data-gift-claim]');
          if (claim) {
            claim.dataset.variantId = variantBtn.dataset.variantId;
            claim.dataset.productId = variantBtn.dataset.productId;
            claim.disabled = false;
            const label = claim.dataset.claimLabel || '';
            claim.textContent = `${label} →`;
            const strike = article.querySelector('[data-gift-price-strike]');
            const formatted = variantBtn.dataset.variantPriceFormatted;
            if (strike && formatted) strike.textContent = formatted;
          }
          return;
        }

        const openBtn = e.target.closest('[data-open-gift-chooser]');
        if (openBtn) {
          e.preventDefault();
          e.stopPropagation();
          const slide = openBtn.closest('[data-gift-tier-slide]');
          if (!slide || slide.dataset.tierEligible !== 'true' || slide.dataset.tierClaimed === 'true') {
            return;
          }
          if (!this.classList.contains('is-open')) {
            if (this.panel) this.panel.removeAttribute('hidden');
            this.classList.add('is-open');
            if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
          }
          this.enterChooserView(openBtn);
          return;
        }

        const closeBtn = e.target.closest('[data-close-gift-chooser]');
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.exitChooserView();
        }
      }

      _initUI() {
        if (this._giftUiBound) return;
        this._giftUiBound = true;

        this.toggleBtn = this.querySelector('[data-gift-toggle]');
        this.panel = this.querySelector('[data-gift-panel]');
        this.blockId = this.dataset.blockId || '';
        this.drawerInner = this.closest('.drawer__inner');
        this._activeChooser = null;
        this._activeTeaser = null;

        if (this.toggleBtn) {
          // Remove any previously attached handler before re-adding to avoid
          // double-fire (open then immediately close) when _initUI is called
          // more than once on the same element instance.
          if (this._toggleClickHandler) {
            this.toggleBtn.removeEventListener('click', this._toggleClickHandler);
          }
          this._toggleClickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const willOpen = !this.classList.contains('is-open');
            if (willOpen) {
              if (this.panel) this.panel.removeAttribute('hidden');
              this.toggleBtn.setAttribute('aria-expanded', 'true');
              this.classList.add('is-open');
            } else {
              if (this.panel) this.panel.setAttribute('hidden', '');
              this.toggleBtn.setAttribute('aria-expanded', 'false');
              this.classList.remove('is-open');
              this.exitChooserView();
            }
          };
          this.toggleBtn.addEventListener('click', this._toggleClickHandler);
        }

        if (!this._onDelegatedGiftClickBound) {
          this._onDelegatedGiftClickBound = this._handleDelegatedGiftClick.bind(this);
        }
        this.removeEventListener('click', this._onDelegatedGiftClickBound);
        this.addEventListener('click', this._onDelegatedGiftClickBound);

        if (typeof window.initGiftTierCarousel === 'function') {
          window.initGiftTierCarousel(this);
        }
      }

      enterChooserView(trigger) {
        const slide = trigger.closest('[data-gift-tier-slide]');
        if (!slide) return;
        const teaser = slide.querySelector('[data-tier-teaser]');
        const chooser = slide.querySelector('[data-gift-chooser]');
        if (!chooser) return;

        this._activeTeaser = teaser;
        this._activeChooser = chooser;

        if (!this.drawerInner) this.drawerInner = this.closest('.drawer__inner');
        if (this.panel) this.panel.removeAttribute('hidden');
        this.classList.add('is-open');
        if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
        if (this.drawerInner) this.drawerInner.classList.add('gift-chooser-active');
        this.classList.add('gift-chooser-mode');
        if (teaser) teaser.setAttribute('hidden', '');
        chooser.removeAttribute('hidden');

        // Flush chooser to the top of the drawer (footer margin-top:auto + sticky
        // footer otherwise left a huge gap above this view).
        const scrollChooserToTop = () => {
          if (!this.drawerInner) return;
          this.drawerInner.scrollTop = 0;
          const headerEl = chooser.querySelector('.cart-drawer-gift__chooser-header');
          if (headerEl && typeof headerEl.scrollIntoView === 'function') {
            headerEl.scrollIntoView({ block: 'start', behavior: 'auto' });
          }
        };
        scrollChooserToTop();
        requestAnimationFrame(() => {
          scrollChooserToTop();
          requestAnimationFrame(scrollChooserToTop);
        });
        // Let carousel re-measure after chooser paints (CSS uses height:auto in
        // chooser mode; refresh keeps track/nav in sync when returning to teasers).
        requestAnimationFrame(() => {
          if (this.dataset.useCarousel === 'true' && typeof window.refreshGiftTierCarousel === 'function') {
            try {
              window.refreshGiftTierCarousel(this);
            } catch (_) {}
          }
        });
      }

      exitChooserView() {
        if (this.drawerInner) this.drawerInner.classList.remove('gift-chooser-active');
        this.classList.remove('gift-chooser-mode');
        if (this._activeChooser) {
          this._activeChooser.setAttribute('hidden', '');
        }
        if (this._activeTeaser) {
          this._activeTeaser.removeAttribute('hidden');
        }
        this._activeChooser = null;
        this._activeTeaser = null;
        // syncViewportHeight() sets inline height on the viewport; leaving it
        // after closing the chooser can clip the teaser or leave a stale band
        // when reopening after cart changes (remove gift → add again).
        this.querySelectorAll('.cart-drawer-gift__tiers-viewport').forEach((vp) => {
          vp.style.removeProperty('height');
        });
        if (this.dataset.useCarousel === 'true' && typeof window.refreshGiftTierCarousel === 'function') {
          try {
            window.refreshGiftTierCarousel(this);
          } catch (_) {}
        }
      }

      _applyClaimedStateToSlide(slide, claimButton) {
        if (!slide) return;
        slide.dataset.tierClaimed = 'true';
        slide.dataset.tierEligible = 'false';

        const teaser = slide.querySelector('[data-tier-teaser]');
        if (!teaser) return;

        // Swap state classes on the teaser wrapper
        teaser.classList.remove('cart-drawer-gift__teaser--eligible');
        teaser.classList.add('cart-drawer-gift__teaser--claimed');
        teaser.removeAttribute('data-gift-teaser');
        teaser.setAttribute('data-gift-teaser-selected', '');

        // Replace the inner <button> card with a <div> so it's non-interactive
        const cardBtn = teaser.querySelector('[data-open-gift-chooser]');
        if (cardBtn) {
          const div = document.createElement('div');
          div.className = cardBtn.className
            .replace('cart-drawer-gift__teaser-card--eligible', 'cart-drawer-gift__teaser-card--claimed');
          div.setAttribute('role', 'status');
          div.innerHTML = cardBtn.innerHTML;
          cardBtn.replaceWith(div);

          // Update sub-line to product title (grab from article in chooser)
          const article = claimButton.closest('[data-gift-item]');
          const productTitle = article ? article.querySelector('.cart-drawer-gift__item-title')?.textContent?.trim() : '';
          const sub = div.querySelector('.cart-drawer-gift__teaser-sub');
          if (sub && productTitle) sub.textContent = productTitle;

          // Update CTA to claimed text
          const cta = div.querySelector('.cart-drawer-gift__teaser-cta');
          if (cta) {
            cta.className = cta.className
              .replace('cart-drawer-gift__teaser-cta--eligible', 'cart-drawer-gift__teaser-cta--claimed');
            const claimedLabel = (slide && slide.dataset && slide.dataset.claimedLabel) || 'Gift claimed';
            cta.textContent = claimedLabel.indexOf('✓') === 0 ? claimedLabel : `✓ ${claimedLabel}`;
          }
        }
      }

      claimGift(button) {
        if (!button) return;
        const slide = button.closest('[data-gift-tier-slide]');
        if (!slide || slide.dataset.tierEligible !== 'true' || slide.dataset.tierClaimed === 'true') {
          return;
        }

        const variantId = button.dataset.variantId;
        const productId = button.dataset.productId;
        const tierIdx = parseInt(button.dataset.tierIndex || '1', 10) || 1;
        if (!variantId) return;

        const cartDrawer = document.querySelector('cart-drawer');
        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding...';

        const chooser = button.closest('[data-gift-chooser]');
        const scope = chooser || this;
        scope.querySelectorAll('[data-gift-claim]').forEach((b) => {
          if (b !== button) b.disabled = true;
        });

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('properties[_drawer_gift_selector]', this.blockId);
        formData.append('properties[_drawer_gift_tier_index]', String(tierIdx));
        formData.append('properties[_drawer_gift_product_id]', productId || '');

        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          const ids = cartDrawer
            .getSectionsToRender()
            .map((section) => section.section || section.id);
          formData.append('sections', ids.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((parsed) => {
            if (parsed.status) throw new Error(parsed.description || parsed.message || 'Error');

            // Immediately update the teaser card to "claimed" state so there's
            // no flash of the eligible state while the re-render loads.
            this._applyClaimedStateToSlide(slide, button);

            this.exitChooserView();

            // Collapse the Free Gifts dropdown after a successful claim so the
            // customer's focus returns to the cart body. They can always reopen
            // it to claim another tier.
            if (this.panel) this.panel.setAttribute('hidden', '');
            if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
            this.classList.remove('is-open');

            if (!cartDrawer) {
              window.location.href = window.routes.cart_url;
              return;
            }
            if (parsed && Array.isArray(parsed.items)) {
              this._syncHeaderClaimBadgeFromCart(parsed.items);
            }
            cartDrawer.renderContents(parsed);
            if (parsed && Array.isArray(parsed.items)) {
              requestAnimationFrame(() => {
                const sel = document.querySelector('#CartDrawer cart-drawer-gift-selector');
                if (sel && typeof sel._syncHeaderClaimBadgeFromCart === 'function') {
                  sel._syncHeaderClaimBadgeFromCart(parsed.items);
                }
              });
            }
          })
          .catch(() => {
            button.disabled = false;
            button.textContent = originalText;
            scope.querySelectorAll('[data-gift-claim]').forEach((b) => {
              if (b.dataset.variantId) b.disabled = false;
            });
          });
      }
    }
  );
}
